import { useState, useEffect } from 'react';
import { X, Trash2, CheckCircle2, FilePen, Info, Building2, Users, UserCheck, Euro, Calendar, ArrowLeftRight, MessageSquare, AlertCircle, Loader2, ShieldCheck } from 'lucide-react';
import { Transaction } from '../../types/dashboard';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import BasicInfoForm from '../transaction/forms/BasicInfoForm';
import AgenciesForm from '../transaction/forms/AgenciesForm';
import NegotiatorsForm from '../transaction/forms/NegotiatorsForm';
import AssistantsForm from '../transaction/forms/AssistantsForm';
import FinancialForm from '../transaction/forms/FinancialForm';
import RetrocessionsForm from '../transaction/forms/RetrocessionsForm';
import DatesForm from '../transaction/forms/DatesForm';
import { checkRetrocessionsAgainstNegotiators } from '../../utils/retrocessions';

interface EditTransactionModalProps {
  transaction: Transaction | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export default function EditTransactionModal({ transaction, isOpen, onClose, onSuccess }: EditTransactionModalProps) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isApproved, setIsApproved] = useState(false);
  const [approvedBy, setApprovedBy] = useState<string | null>(null);
  const [approvedAt, setApprovedAt] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    property_number: '',
    mandate_type: 'simple' as 'exclu' | 'simple' | 'privilège' | 'recherche' | 'délégation',
    property_origin: '' as 'gestion' | 'lefeuvre' | 'newco' | 'syndic' | '',
    copro_id: '',
    copro_newco_id: '',
    copro_syndic_id: '',
    lefeuvre_syndic: false,
    lefeuvre_gestion: false,
    newco_syndic: false,
    newco_gestion: false,
    syndic_syndic: false,
    syndic_gestion: false,
    lead_origin: '' as 'acquereur' | 'prospection_boitage' | 'prospection_porte_a_porte' | 'prospection_pige' | 'prospection_preavis_gestion' | 'prospection_rel_copro' | 'prospection_panneau_picking' | 'prospection_capturama_ademe' | 'prospection_ancien_client_signe' | 'spontane_gestion_loc' | 'spontane_appel_agence' | 'spontane_passage_agence' | 'spontane_reseau_perso' | 'spontane_syndic' | 'spontane_immobilier_entreprise' | 'spontane_temps_fort_campagne_publipostage' | 'spontane_interagence_transac' | 'relance_ancien_client_copro' | 'spontane_mail_boite_transaction' | 'spontane_flyer' | 'relance_ancienne_estim_source_inconnue' | '',
    seller: '',
    buyer: '',
    sale_price: '',
    fees_with_tax: '',
    fees_without_tax: '',
    agencies: [{ name: '' }],
    negotiators: [{ id: '', role: 'vendeur' as 'apporteur' | 'vendeur', percentage: 100, name: '', agency: '' }],
    assistants: [
      { id: '', role: 'entrante' as 'entrante' | 'sortante' },
      { id: '', role: 'sortante' as 'entrante' | 'sortante' }
    ],
    retrocessions: [] as {
      type: 'champagne' | 'ambassadeur' | 'diagnostic' | 'recommandation_interne' | 'uff' | 'retrocession_lefeuvre' | 'retrocession_newco' | 'retrocession_syndic' | 'duo_coaching' | 'intercabinet' | 'geste_commercial' | 'autre';
      amount: string;
      description?: string;
      payment_date?: string | null;
      distribution_type: 'equal' | 'custom' | 'single';
      primary_negotiator_id: string;
      secondary_negotiator_id?: string | null;
      tertiary_negotiator_id?: string | null;
      quaternary_negotiator_id?: string | null;
      distribution_percentages?: {
        [key: string]: number;
        total: number;
      };
    }[],
    compromise_date: '',
    expected_acte_date: '',
    acte_date: '',
    invoice_date: '',
    invoice_number: '',
    encaissement_date: '',
    mandate_date: '',
    property_type: '' as '' | 'Appartement' | 'Maison' | 'Immeuble' | 'Parking' | 'Box' | 'Terrain',
    comments_transac: ''
  });

  useEffect(() => {
    if (transaction?.id) {
      loadTransactionDetails(transaction.id);
    }
  }, [transaction]);

  const loadTransactionDetails = async (transactionId: string) => {
    try {
      setLoading(true);
      
      const { data: transactionData, error: transactionError } = await supabase
        .from('transactions')
        .select(`
          *,
          approved_by:users!approved_by (
            firstname,
            lastname
          ),
          transaction_agencies (
            agency
          ),
          transaction_negotiators (
            negotiator:negotiators (
              id,
              firstname,
              lastname
            ),
            role,
            percentage,
            agency
          ),
          transaction_assistants (
            assistant:assistants (
              id,
              firstname,
              lastname
            ),
            role
          ),
          transaction_retrocessions (
            id,
            type,
            amount,
            description,
            payment_date,
            primary_negotiator_id,
            secondary_negotiator_id,
            tertiary_negotiator_id,
            quaternary_negotiator_id,
            distribution_type,
            distribution_percentages
          )
        `)
        .eq('id', transactionId)
        .single();

      if (transactionError) throw transactionError;
      if (!transactionData) throw new Error('Transaction not found');

      setIsApproved(transactionData.approved || false);
      setApprovedBy(transactionData.approved_by ? `${transactionData.approved_by.firstname} ${transactionData.approved_by.lastname}` : null);
      setApprovedAt(transactionData.approved_at);
      
      setFormData({
        property_number: transactionData.property_number,
        mandate_type: transactionData.mandate_type || 'simple',
        property_origin: transactionData.property_origin || '',
        copro_id: transactionData.copro_id || '',
        copro_newco_id: transactionData.copro_newco_id || '',
        copro_syndic_id: transactionData.copro_syndic_id || '',
        lefeuvre_syndic: transactionData.lefeuvre_syndic || false,
        lefeuvre_gestion: transactionData.lefeuvre_gestion || false,
        newco_syndic: transactionData.newco_syndic || false,
        newco_gestion: transactionData.newco_gestion || false,
        syndic_syndic: transactionData.syndic_syndic || false,
        syndic_gestion: transactionData.syndic_gestion || false,
        lead_origin: transactionData.lead_origin || '',
        seller: transactionData.seller,
        buyer: transactionData.buyer,
        sale_price: transactionData.sale_price.toString(),
        fees_with_tax: transactionData.fees_with_tax.toString(),
        fees_without_tax: transactionData.fees_without_tax.toString(),
        agencies: transactionData.transaction_agencies?.map(ta => ({ 
          name: ta.agency 
        })) || [{ name: '' }],
        negotiators: transactionData.transaction_negotiators?.map(tn => ({
          id: tn.negotiator.id,
          name: `${tn.negotiator.firstname} ${tn.negotiator.lastname}`,
          role: tn.role,
          percentage: tn.percentage,
          // Sans l'agence, un négociateur présent dans plusieurs agences de la vente
          // s'affichait vide et repartait à null à l'enregistrement
          agency: tn.agency || ''
        })) || [{ id: '', role: 'vendeur', percentage: 100, name: '', agency: '' }],
        assistants: transactionData.transaction_assistants?.map(ta => ({
          id: ta.assistant.id,
          role: ta.role
        })) || [
          { id: '', role: 'entrante' },
          { id: '', role: 'sortante' }
        ],
        retrocessions: transactionData.transaction_retrocessions?.map(tr => ({
          type: tr.type,
          amount: tr.amount.toString(),
          description: tr.description || '',
          payment_date: tr.payment_date || null,
          distribution_type: tr.distribution_type || 'single',
          primary_negotiator_id: tr.primary_negotiator_id || '',
          secondary_negotiator_id: tr.secondary_negotiator_id || null,
          tertiary_negotiator_id: tr.tertiary_negotiator_id || null,
          quaternary_negotiator_id: tr.quaternary_negotiator_id || null,
          distribution_percentages: tr.distribution_percentages
        })) || [],
        compromise_date: transactionData.compromise_date || '',
        expected_acte_date: transactionData.expected_acte_date || '',
        acte_date: transactionData.acte_date || '',
        invoice_date: transactionData.invoice_date || '',
        invoice_number: transactionData.invoice_number || '',
        encaissement_date: transactionData.encaissement_date || '',
        mandate_date: transactionData.mandate_date ? String(transactionData.mandate_date).split('T')[0] : '',
        property_type: transactionData.property_type || '',
        comments_transac: transactionData.comments_transac || ''
      });
    } catch (err: any) {
      console.error('Error loading transaction details:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!transaction || !user) return;

    setLoading(true);
    setError('');

    try {
      const totalPercentage = formData.negotiators.reduce((sum, n) => sum + n.percentage, 0);
      if (totalPercentage !== 100) {
        throw new Error('Le total des pourcentages doit être égal à 100%');
      }

      // Garde-fou : les négociateurs des rétrocessions doivent être ceux de la vente.
      // Sans ce contrôle, remplacer un négociateur laissait sa part sur la rétrocession :
      // le montant n'était plus imputé à personne et disparaissait des statistiques.
      const retrocessionProblems = checkRetrocessionsAgainstNegotiators(
        formData.retrocessions,
        formData.negotiators.filter(n => n.id)
      );
      if (retrocessionProblems.length > 0) {
        throw new Error(retrocessionProblems.join(' '));
      }

      const transactionData = {
        property_number: formData.property_number,
        mandate_type: formData.mandate_type,
        property_origin: formData.property_origin || null,
        copro_id: formData.copro_id || null,
        copro_newco_id: formData.copro_newco_id || null,
        copro_syndic_id: formData.copro_syndic_id || null,
        lefeuvre_syndic: formData.lefeuvre_syndic,
        lefeuvre_gestion: formData.lefeuvre_gestion,
        newco_syndic: formData.newco_syndic,
        newco_gestion: formData.newco_gestion,
        syndic_syndic: formData.syndic_syndic,
        syndic_gestion: formData.syndic_gestion,
        lead_origin: formData.lead_origin || null,
        seller: formData.seller,
        buyer: formData.buyer,
        sale_price: parseFloat(formData.sale_price),
        fees_with_tax: parseFloat(formData.fees_with_tax),
        fees_without_tax: parseFloat(formData.fees_without_tax),
        compromise_date: formData.compromise_date || null,
        expected_acte_date: formData.expected_acte_date || null,
        acte_date: formData.acte_date || null,
        invoice_date: formData.invoice_date || null,
        invoice_number: formData.invoice_number || null,
        encaissement_date: formData.encaissement_date || null,
        mandate_date: formData.mandate_date || null,
        property_type: formData.property_type || null,
        comments_transac: formData.comments_transac || null,
        approved: isApproved,
        approved_by: isApproved ? user.id : null,
        approved_at: isApproved ? new Date().toISOString() : null
      };

      const { error: updateError } = await supabase
        .from('transactions')
        .update(transactionData)
        .eq('id', transaction.id);

      if (updateError) throw updateError;

      // Update agencies
      await supabase
        .from('transaction_agencies')
        .delete()
        .eq('transaction_id', transaction.id);

      await supabase
        .from('transaction_agencies')
        .insert(
          formData.agencies
            .filter(a => a.name)
            .map(a => ({
              transaction_id: transaction.id,
              agency: a.name
            }))
        );

      // Update negotiators
      await supabase
        .from('transaction_negotiators')
        .delete()
        .eq('transaction_id', transaction.id);

      await supabase
        .from('transaction_negotiators')
        .insert(
          formData.negotiators
            .filter(n => n.id)
            .map(n => ({
              transaction_id: transaction.id,
              negotiator_id: n.id,
              role: n.role,
              percentage: n.percentage,
              agency: n.agency || null
            }))
        );

      // Update assistants
      await supabase
        .from('transaction_assistants')
        .delete()
        .eq('transaction_id', transaction.id);

      await supabase
        .from('transaction_assistants')
        .insert(
          formData.assistants
            .filter(a => a.id)
            .map(a => ({
              transaction_id: transaction.id,
              assistant_id: a.id,
              role: a.role
            }))
        );

      // Update retrocessions
      await supabase
        .from('transaction_retrocessions')
        .delete()
        .eq('transaction_id', transaction.id);

      if (formData.retrocessions.length > 0) {
        const retrocessionsToInsert = formData.retrocessions.map(r => ({
          transaction_id: transaction.id,
          type: r.type,
          amount: parseFloat(r.amount),
          description: r.description || null,
          payment_date: r.payment_date || null,
          primary_negotiator_id: r.primary_negotiator_id,
          secondary_negotiator_id: r.secondary_negotiator_id || null,
          tertiary_negotiator_id: r.tertiary_negotiator_id || null,
          quaternary_negotiator_id: r.quaternary_negotiator_id || null,
          distribution_type: r.distribution_type,
          distribution_percentages: r.distribution_percentages || null
        }));

        const { error: retrocessionsError } = await supabase
          .from('transaction_retrocessions')
          .insert(retrocessionsToInsert);

        if (retrocessionsError) {
          console.error('Error inserting retrocessions:', retrocessionsError);
          throw retrocessionsError;
        }
      }

      // Add log for approval if transaction was approved
      if (isApproved && !transaction.approved) {
        // Envoyer une notification email pour la validation
        try {
          console.log('Sending validation notification email');
          await sendStatusChangeNotification(transaction.id, transaction.status, transaction.status);
          console.log('Validation notification sent successfully');
        } catch (emailError) {
          console.error('Error sending validation notification:', emailError);
          // Ne pas faire échouer la mise à jour si l'email échoue
        }

        await supabase
          .from('transaction_logs')
          .insert([{
            transaction_id: transaction.id,
            user_id: user.id,
            event_type: 'validation',
            event_data: {
              action: 'approved',
              approved_by: `${user.firstname} ${user.lastname}`,
              approved_at: new Date().toISOString()
            }
          }]);
      }

      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Error in handleSubmit:', err);
      setError(err.message || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!transaction || !user) return;
    
    if (!confirm('Êtes-vous sûr de vouloir supprimer cette transaction ? Cette action est irréversible.')) {
      return;
    }

    try {
      setLoading(true);
      
      const { error: deleteError } = await supabase
        .from('transactions')
        .delete()
        .eq('id', transaction.id);

      if (deleteError) throw deleteError;

      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Error deleting transaction:', err);
      setError(err.message || 'Une erreur est survenue lors de la suppression');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !transaction) return null;

  const STATUS_STYLES: Record<string, { bg: string; text: string; dot: string; label: string }> = {
    potentiel: { bg: 'bg-slate-100', text: 'text-slate-700', dot: 'bg-slate-400', label: 'Potentiel' },
    compromis: { bg: 'bg-amber-100', text: 'text-amber-700', dot: 'bg-amber-500', label: 'Compromis' },
    acte:      { bg: 'bg-blue-100',  text: 'text-blue-700',  dot: 'bg-blue-500',  label: 'Acte' },
    encaisse:  { bg: 'bg-emerald-100', text: 'text-emerald-700', dot: 'bg-emerald-500', label: 'Encaissé' },
    cloture:   { bg: 'bg-indigo-100', text: 'text-indigo-700', dot: 'bg-indigo-500', label: 'Clôturé' },
    annule:    { bg: 'bg-red-100',   text: 'text-red-700',   dot: 'bg-red-500',   label: 'Annulé' },
  };
  const statusStyle = STATUS_STYLES[transaction.status] ?? STATUS_STYLES.potentiel;

  return (
    <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-sm flex items-end sm:items-center justify-center z-[200] p-0 sm:p-4">
      <form
        onSubmit={handleSubmit}
        className="bg-white w-full sm:max-w-4xl sm:rounded-2xl flex flex-col overflow-hidden shadow-2xl"
        style={{ maxHeight: 'min(92vh, 100dvh)' }}
      >
        {/* ── Header ── */}
        <div className="flex-none bg-gradient-to-r from-slate-700 to-slate-900 px-6 py-5">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center shrink-0">
                <FilePen size={20} className="text-white" />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="text-lg font-bold text-white leading-tight">
                    Transaction #{transaction.property_number}
                  </h2>
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${statusStyle.bg} ${statusStyle.text}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${statusStyle.dot}`} />
                    {statusStyle.label}
                  </span>
                </div>
                <p className="text-slate-400 text-xs mt-0.5">Modifier les informations · Vue administration</p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="w-9 h-9 rounded-xl bg-white/15 hover:bg-white/25 flex items-center justify-center transition-colors shrink-0"
            >
              <X size={18} className="text-white" />
            </button>
          </div>
        </div>

        {/* ── Scrollable content ── */}
        <div className="flex-1 overflow-y-auto bg-slate-50 p-5 space-y-4">

          {/* Validation dossier */}
          {['compromis', 'acte', 'encaisse', 'cloture'].includes(transaction.status) && (
            <div className={`rounded-xl border overflow-hidden ${isApproved ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200 bg-white'} shadow-sm transition-colors`}>
              <div className={`px-5 py-3 border-b flex items-center gap-2.5 ${isApproved ? 'bg-emerald-100 border-emerald-200' : 'bg-slate-50 border-slate-200'}`}>
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${isApproved ? 'bg-emerald-200 text-emerald-700' : 'bg-slate-200 text-slate-600'}`}>
                  <ShieldCheck size={14} />
                </div>
                <h3 className={`text-xs font-bold uppercase tracking-wider ${isApproved ? 'text-emerald-700' : 'text-slate-600'}`}>
                  Validation du dossier
                </h3>
              </div>
              <div className="p-5">
                <label className="flex items-center gap-3 cursor-pointer select-none">
                  <div className={`w-10 h-6 rounded-full transition-colors relative ${isApproved ? 'bg-emerald-500' : 'bg-slate-300'}`}>
                    <div className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-transform ${isApproved ? 'translate-x-5' : 'translate-x-1'}`} />
                    <input
                      type="checkbox"
                      checked={isApproved}
                      onChange={(e) => setIsApproved(e.target.checked)}
                      className="sr-only"
                    />
                  </div>
                  <div>
                    <span className="text-sm font-semibold text-slate-800">
                      {isApproved ? 'Dossier validé' : 'Valider le dossier'}
                    </span>
                    {approvedBy && approvedAt ? (
                      <p className="text-xs text-emerald-600 mt-0.5 flex items-center gap-1">
                        <CheckCircle2 size={12} />
                        Validé par {approvedBy} le {new Date(approvedAt).toLocaleString('fr-FR')}
                      </p>
                    ) : (
                      <p className="text-xs text-slate-400 mt-0.5">Cocher pour marquer ce dossier comme validé</p>
                    )}
                  </div>
                </label>
              </div>
            </div>
          )}

          {/* Informations générales */}
          <div className="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
            <div className="px-5 py-3 border-b border-indigo-100 bg-indigo-50 flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-lg bg-indigo-100 flex items-center justify-center text-indigo-600"><Info size={14} /></div>
              <h3 className="text-xs font-bold uppercase tracking-wider text-indigo-700">Informations générales</h3>
            </div>
            <div className="p-5"><BasicInfoForm formData={formData} onChange={setFormData} currentTransactionId={transaction?.id} /></div>
          </div>

          {/* Agences */}
          <div className="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
            <div className="px-5 py-3 border-b border-blue-100 bg-blue-50 flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-lg bg-blue-100 flex items-center justify-center text-blue-600"><Building2 size={14} /></div>
              <h3 className="text-xs font-bold uppercase tracking-wider text-blue-700">Agences</h3>
            </div>
            <div className="p-5"><AgenciesForm formData={formData} onChange={setFormData} /></div>
          </div>

          {/* Négociateurs */}
          <div className="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
            <div className="px-5 py-3 border-b border-emerald-100 bg-emerald-50 flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-lg bg-emerald-100 flex items-center justify-center text-emerald-600"><Users size={14} /></div>
              <h3 className="text-xs font-bold uppercase tracking-wider text-emerald-700">Négociateurs</h3>
            </div>
            <div className="p-5"><NegotiatorsForm formData={formData} onChange={setFormData} /></div>
          </div>

          {/* Assistantes */}
          <div className="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
            <div className="px-5 py-3 border-b border-amber-100 bg-amber-50 flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-lg bg-amber-100 flex items-center justify-center text-amber-600"><UserCheck size={14} /></div>
              <h3 className="text-xs font-bold uppercase tracking-wider text-amber-700">Assistantes</h3>
            </div>
            <div className="p-5"><AssistantsForm formData={formData} onChange={setFormData} /></div>
          </div>

          {/* Finances */}
          <div className="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
            <div className="px-5 py-3 border-b border-teal-100 bg-teal-50 flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-lg bg-teal-100 flex items-center justify-center text-teal-600"><Euro size={14} /></div>
              <h3 className="text-xs font-bold uppercase tracking-wider text-teal-700">Informations financières</h3>
            </div>
            <div className="p-5"><FinancialForm formData={formData} onChange={setFormData} /></div>
          </div>

          {/* Dates */}
          <div className="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
            <div className="px-5 py-3 border-b border-violet-100 bg-violet-50 flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-lg bg-violet-100 flex items-center justify-center text-violet-600"><Calendar size={14} /></div>
              <h3 className="text-xs font-bold uppercase tracking-wider text-violet-700">Dates</h3>
            </div>
            <div className="p-5"><DatesForm formData={formData} onChange={setFormData} /></div>
          </div>

          {/* Rétrocessions */}
          <div className="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
            <div className="px-5 py-3 border-b border-rose-100 bg-rose-50 flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-lg bg-rose-100 flex items-center justify-center text-rose-600"><ArrowLeftRight size={14} /></div>
              <h3 className="text-xs font-bold uppercase tracking-wider text-rose-700">Rétrocessions</h3>
            </div>
            <div className="p-5"><RetrocessionsForm formData={formData} onChange={setFormData} /></div>
          </div>

          {/* Commentaire */}
          <div className="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
            <div className="px-5 py-3 border-b border-slate-200 bg-slate-50 flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-lg bg-slate-200 flex items-center justify-center text-slate-600"><MessageSquare size={14} /></div>
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-700">Commentaire</h3>
            </div>
            <div className="p-5">
              <textarea
                value={formData.comments_transac || ''}
                onChange={(e) => setFormData({ ...formData, comments_transac: e.target.value })}
                placeholder="Informations complémentaires sur la transaction..."
                rows={4}
                className="w-full px-3.5 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-400 resize-y text-sm transition-colors"
              />
            </div>
          </div>
        </div>

        {/* ── Footer ── */}
        <div className="flex-none border-t border-slate-200 bg-white px-6 py-4 flex items-center gap-3">
          {/* Bouton supprimer à gauche */}
          <button
            type="button"
            onClick={handleDelete}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-xl hover:bg-red-100 disabled:opacity-50 transition-colors"
          >
            <Trash2 size={15} />
            Supprimer
          </button>

          {/* Erreur */}
          {error && (
            <div className="flex items-center gap-2 text-red-600 text-sm min-w-0 flex-1">
              <AlertCircle size={14} className="shrink-0" />
              <span className="truncate">{error}</span>
            </div>
          )}

          {/* Actions droite */}
          <div className="flex gap-3 ml-auto shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-slate-700 to-slate-900 rounded-xl hover:from-slate-800 hover:to-black disabled:opacity-60 transition-all shadow-sm"
            >
              {loading && <Loader2 size={14} className="animate-spin" />}
              {loading ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}